import { useMemo } from "react";

import { useIntegratorMe } from "@/lib/me-query";
import {
  hubModuleAllowsAdminSettings,
  hubModuleAllowsAdminSettingsAny,
  hubModuleAllowsWrite,
  hubModuleAllowsWriteAny,
  hubModuleHasAnyAccess,
  hubModuleHasAnyAccessAny,
} from "@/lib/subapp-access";

/** Module keys that gate the same manufacturing APIs as legacy **Manufacturing** plus **Manufacturing management**. */
export const MANUFACTURING_SCOPE_MODULE_KEYS = ["manufacturing", "manufacturing_management"] as const;

/**
 * Whether the signed-in integrator may perform hub writes (HTTP mutations) for the given module key.
 * Mirrors `hubModuleAllowsWrite` against `/api/me` (`integratorSubappAccess`). False while the profile is loading.
 */
export function useHubModuleWrite(appKey: string): boolean {
  const query = useIntegratorMe();
  return useMemo(() => hubModuleAllowsWrite(query.data, appKey), [query.data, appKey]);
}

/** Read access (any tier) for hub module APIs and read-only UI tabs. */
export function useHubModuleHasAnyAccess(appKey: string): boolean {
  const query = useIntegratorMe();
  return useMemo(() => hubModuleHasAnyAccess(query.data, appKey), [query.data, appKey]);
}

/** Write access when **any** of the listed modules grants read-write or administrator tier. */
export function useHubModuleWriteAny(appKeys: readonly string[]): boolean {
  const query = useIntegratorMe();
  return useMemo(() => hubModuleAllowsWriteAny(query.data, appKeys), [query.data, appKeys]);
}

/** Write gate for pages and mutations shared by `/manufacturing` and `/manufacturing-management` hubs. */
export function useManufacturingScopeHubWrite(): boolean {
  return useHubModuleWriteAny(MANUFACTURING_SCOPE_MODULE_KEYS);
}

/** Module administrator tier (hub settings) or integrator administrator. */
export function useHubModuleAdminSettings(appKey: string): boolean {
  const query = useIntegratorMe();
  return useMemo(() => hubModuleAllowsAdminSettings(query.data, appKey), [query.data, appKey]);
}

export function useHubModuleHasAnyAccessAny(appKeys: readonly string[]): boolean {
  const query = useIntegratorMe();
  return useMemo(() => hubModuleHasAnyAccessAny(query.data, appKeys), [query.data, appKeys]);
}

/** Module administrator on any listed module, or integrator administrator. */
export function useHubModuleAdminSettingsAny(appKeys: readonly string[]): boolean {
  const query = useIntegratorMe();
  return useMemo(() => hubModuleAllowsAdminSettingsAny(query.data, appKeys), [query.data, appKeys]);
}
